import React from "react";
import { Button, Col, Form, Row, Space, Spin, Typography } from "antd";
import { ReactComponent as BluetoothIcon } from "../../assets/icons/bluetooth.svg";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";
import "./index.css";
import { Device, WifiConnectArgs } from "../../types";
import { BLUETOOTH_STATES } from "./const";
import DeviceSetupForm from "./DeviceSetupForm";
import WifiConnectedTag from "./WifiConnectedTag";

type Props = {
  selectedDevice?: Device;
  bluetoothState: string;
  handleConnectButtonPressed: (values: WifiConnectArgs) => Promise<void>;
  handleScanButtonPressed: () => Promise<void>;
  handleRescanWifiButtonPressed: () => void;
  handleCancelButtonPressed: () => void;
  wifiList: string[];
  currentNetwork: string | null;
  isWifiConnected: boolean;
};

const { Title } = Typography;

const BluetoothSetup: React.FC<Props> = ({
  selectedDevice,
  bluetoothState,
  handleConnectButtonPressed,
  handleScanButtonPressed,
  handleRescanWifiButtonPressed,
  handleCancelButtonPressed,
  isWifiConnected,
  ...props
}) => {
  const [form] = Form.useForm<WifiConnectArgs>();

  const onCancelButtonPressed = () => {
    form.resetFields();
    handleCancelButtonPressed();
  };

  // buttons
  const scanButton =
    bluetoothState === BLUETOOTH_STATES.DISCONNECTED ? (
      <Row justify={"center"} align={"middle"}>
        <Col flex="auto">
          <Row>
            <Button
              onClick={handleScanButtonPressed}
              type="primary"
              shape="round"
              about="Scan"
              className="device-setup-scan-button"
              icon={<BluetoothIcon className="bluetooth-icon" />}
              block
            />
          </Row>
          <Row>
            <h3>Connect Device</h3>
          </Row>
        </Col>
      </Row>
    ) : null;

  const cancelButton = (
    <Button
      onClick={onCancelButtonPressed}
      danger
      shape="round"
      about="Cancel"
      icon={<CloseOutlined className="device-setup-icon-button" />}
    />
  );

  const rescanButton =
    bluetoothState === BLUETOOTH_STATES.CONNECTED ? (
      <Button
        onClick={handleRescanWifiButtonPressed}
        shape="round"
        about="Rescan"
        icon={<SyncOutlined className="device-setup-icon-button" />}
      />
    ) : (
      <Spin />
    );

  const main = (
    <>
      {scanButton}
      <DeviceSetupForm
        form={form}
        bluetoothState={bluetoothState}
        isWifiConnected={isWifiConnected}
        rescanButton={rescanButton}
        cancelButton={cancelButton}
        handleFinish={handleConnectButtonPressed}
        {...props}
      />
    </>
  );

  return (
    <Space
      size="large"
      className={"flex flex-col mt-6 h-72.5 w-137.5"}
      id={"bluetooth-setup"}
    >
      <Title level={3}>
        {" "}
        Device:
        {selectedDevice?.display_name ? selectedDevice?.display_name : ""}
      </Title>
      <WifiConnectedTag
        isWifiConnected={isWifiConnected}
        show={bluetoothState === BLUETOOTH_STATES.CONNECTED}
      />
      {bluetoothState === BLUETOOTH_STATES.UNSUPPORTED ? (
        <h3>
          This browser does not support WebBluetooth. Please use a supported
          browser such as Google Chrome for this feature.
        </h3>
      ) : (
        main
      )}
    </Space>
  );
};

export default BluetoothSetup;
